<template>
  <div class="sv-grid-wrap" data-test="sv-grid">
    <div class="sv-grid" :style="gridStyle">
      <!-- Header row: sticky so the operatory names stay visible while the day scrolls. -->
      <div class="sv-grid__corner"></div>
      <div
        v-for="op in operatories"
        :key="`h-${op.OperatoryNum}`"
        class="sv-grid__ophead"
        :style="{ borderTopColor: accentFor(op.OperatoryNum) }"
        :data-test="`sv-ophead-${op.OperatoryNum}`"
      >
        <span class="sv-grid__opname" :title="op.OpName">{{ op.OpName }}</span>
        <span class="sv-grid__provs">
          <span
            v-for="p in providersFor(op.OperatoryNum)"
            :key="`${op.OperatoryNum}-${p.ProvNum}`"
            class="sv-grid__prov"
            :style="{ background: accentFor(op.OperatoryNum) }"
            :title="p.full"
          >{{ p.initials }}</span>
        </span>
      </div>

      <!-- Time gutter -->
      <div class="sv-grid__gutter" :style="{ height: `${bodyHeight}px` }">
        <div
          v-for="h in hours"
          :key="`t-${h}`"
          class="sv-grid__hour-label"
          :style="{ height: `${hourHeight}px` }"
        >{{ hourLabel(h) }}</div>
      </div>

      <!-- One column per operatory -->
      <div
        v-for="op in operatories"
        :key="`c-${op.OperatoryNum}`"
        class="sv-grid__col"
        :style="{ height: `${bodyHeight}px`, background: tintFor(op.OperatoryNum) }"
        :data-test="`sv-col-${op.OperatoryNum}`"
      >
        <div
          v-for="h in hours"
          :key="`r-${op.OperatoryNum}-${h}`"
          class="sv-grid__hour-row"
          :style="{ height: `${hourHeight}px` }"
        ></div>

        <div
          v-for="b in blockoutsFor(op.OperatoryNum)"
          :key="`b-${b.ScheduleNum}-${op.OperatoryNum}`"
          class="sv-grid__blockout"
          :style="blockoutStyle(b)"
          :title="b.BlockoutName || 'Blockout'"
        >{{ b.BlockoutName }}</div>

        <AppointmentBlock
          v-for="appt in columns[op.OperatoryNum] || []"
          :key="appt.AptNum"
          :appt="appt"
          :tier="tierFor(appt)"
          :view-settings="viewSettings"
          :bucket-map="bucketMap"
          :alert-payload="alertPayload"
          :accent="accentFor(op.OperatoryNum)"
          :top="geometryFor(appt).top"
          :height="geometryFor(appt).height"
          :lane="appt.lane || 0"
          :lane-count="appt.laneCount || 1"
          :checklist="checklists[appt.AptNum] || null"
          :checklist-mode="checklistMode"
          @select="$emit('select-appointment', $event)"
          @card-enter="$emit('card-enter', $event)"
          @card-leave="$emit('card-leave', $event)"
          @alert-focus="$emit('alert-focus', $event)"
          @open-checklist="$emit('open-checklist', $event)"
        />

        <!-- The NOW line sits above the hour rows but below the blocks. -->
        <div
          v-if="nowTop !== null"
          class="sv-grid__now"
          :style="{ top: `${nowTop}px` }"
          aria-hidden="true"
        ></div>
      </div>
    </div>

    <p v-if="unplaced.length" class="sv-grid__unplaced" data-test="sv-unplaced">
      {{ unplaced.length }}
      {{ unplaced.length === 1 ? 'appointment is' : 'appointments are' }}
      not in a visible operatory (hidden in Open Dental, or no chair assigned) and
      {{ unplaced.length === 1 ? 'is' : 'are' }} not drawn above.
    </p>
  </div>
</template>

<script>
/**
 * The operatory grid: a time gutter plus one column per non-hidden operatory, in Open Dental's own
 * ItemOrder, so the layout reads like the office's OD screen.
 *
 * All the maths lives in src/utils/scheduleView/layout.js — pure, import-free and node-tested. This
 * component only turns the numbers into styles.
 */
import AppointmentBlock from './AppointmentBlock.vue';
import {
  computeDayWindow, groupByOperatory, blockGeometry, densityTier,
  hourRows, nowLineTop, timeToMinutes, hourLabel as formatHourLabel, operatoryPalette,
  OPERATORY_TINTS, OPERATORY_ACCENTS,
} from '@/utils/scheduleView/layout';

const HOUR_HEIGHT = 76;

export default {
  name: 'ScheduleGrid',

  components: { AppointmentBlock },

  props: {
    /** The /schedule-view/day payload, already narrowed by the filters. */
    day: { type: Object, required: true },
    /**
     * The UNFILTERED payload, used only to choose the grid's hours.
     *
     * The day's shape must not change when somebody narrows to one provider: a grid that collapses
     * from 7am–6pm to 9am–11am as you toggle a filter is unreadable, and it hides the fact that the
     * practice works the rest of the day. Defaults to `day` when nothing is filtered.
     */
    unfilteredDay: { type: Object, default: null },
    viewSettings: { type: Object, required: true },
    bucketMap: { type: Object, default: () => ({}) },
    /** The /schedule-view/alerts payload, passed straight through to each block. */
    alertPayload: { type: Object, default: null },
    /** Minutes past midnight in the SITE's timezone, or null when not viewing today. */
    nowMinutes: { type: Number, default: null },
    isToday: { type: Boolean, default: false },
    /**
     * The visit checklists the page built for this day, AptNum → { categories, totals }. Built once
     * at the page from the same alert keys the blocks draw (checklist.js checklistsForDay).
     */
    checklists: { type: Object, default: () => ({}) },
    /** 'hover' | 'pop' | 'sheet', decided by the page's ChecklistLayer. */
    checklistMode: { type: String, default: 'hover' },
  },

  data() {
    return { hourHeight: HOUR_HEIGHT };
  },

  computed: {
    operatories() {
      return this.day.operatories || [];
    },

    window() {
      const source = this.unfilteredDay || this.day;
      return computeDayWindow({
        appointments: source.appointments,
        providerHours: source.providerHours,
        blockouts: this.viewSettings.showBlockouts ? source.blockouts : [],
      });
    },

    hours() {
      return hourRows(this.window.startHour, this.window.endHour);
    },

    bodyHeight() {
      return this.hours.length * this.hourHeight;
    },

    grouped() {
      return groupByOperatory(this.day.appointments || [], this.operatories);
    },

    columns() {
      return this.grouped.columns;
    },

    unplaced() {
      return this.grouped.unplaced;
    },

    gridStyle() {
      return {
        gridTemplateColumns: `56px repeat(${this.operatories.length}, minmax(180px, 1fr))`,
      };
    },

    nowTop() {
      return nowLineTop({
        isToday: this.isToday,
        nowMinutes: this.nowMinutes,
        startHour: this.window.startHour,
        endHour: this.window.endHour,
        hourHeight: this.hourHeight,
      });
    },

    /** OperatoryNum -> colours, from the UNFILTERED day so a chair keeps its colour when filtered. */
    palette() {
      return operatoryPalette((this.unfilteredDay || this.day).operatories);
    },

    /** ProvNum -> provider, for the header chips. */
    providerIndex() {
      const idx = {};
      (this.day.providers || []).forEach((p) => { idx[p.ProvNum] = p; });
      return idx;
    },
  },

  methods: {
    tintFor(operatoryNum) {
      const c = this.palette[operatoryNum];
      return c ? c.tint : OPERATORY_TINTS[0];
    },

    accentFor(operatoryNum) {
      const c = this.palette[operatoryNum];
      return c ? c.accent : OPERATORY_ACCENTS[0];
    },

    hourLabel(h) {
      return formatHourLabel(h);
    },

    geometryFor(appt) {
      return blockGeometry(appt, {
        startHour: this.window.startHour,
        hourHeight: this.hourHeight,
      }) || { top: 0, height: 0 };
    },

    tierFor(appt) {
      return densityTier(this.geometryFor(appt).height);
    },

    /**
     * Who is working this chair today, derived from the day's own appointments rather than from
     * provider hours: OD's schedule rows are per provider, not per operatory, so they cannot say
     * which chair somebody is in. The appointments can, and they are the truth the office sees.
     */
    providersFor(operatoryNum) {
      const seen = [];
      const out = [];
      (this.columns[operatoryNum] || []).forEach((appt) => {
        [appt.ProvNum, appt.ProvHyg].forEach((provNum) => {
          if (!provNum || seen.indexOf(provNum) !== -1) return;
          const p = this.providerIndex[provNum];
          if (!p) return;
          seen.push(provNum);
          out.push({
            ProvNum: provNum,
            initials: p.Abbr || `${(p.FName || '?')[0]}${(p.LName || '')[0] || ''}`,
            full: `${p.FName || ''} ${p.LName || ''}`.trim(),
          });
        });
      });
      return out.slice(0, 3);
    },

    blockoutsFor(operatoryNum) {
      if (!this.viewSettings.showBlockouts) return [];
      // A blockout with no scheduleop row covers EVERY operatory — Open Dental permits that, and
      // dropping it would hide a whole-practice block (lunch, a staff meeting).
      return (this.day.blockouts || []).filter(
        b => b.OperatoryNum === operatoryNum || b.OperatoryNum === null || b.OperatoryNum === undefined
      );
    },

    blockoutStyle(b) {
      const start = timeToMinutes(b.StartTime);
      const end = timeToMinutes(b.StopTime);
      if (start === null || end === null) return { display: 'none' };
      const perMinute = this.hourHeight / 60;
      return {
        top: `${(start - this.window.startHour * 60) * perMinute}px`,
        height: `${Math.max(8, (end - start) * perMinute)}px`,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-grid-wrap {
  overflow: auto;
  background: $sv-bg;
  border: 1px solid $sv-line;
  border-radius: $sv-radius-lg;
}

.sv-grid {
  display: grid;
  min-width: max-content;
}

.sv-grid__corner {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 5;
  background: $sv-bg;
  border-bottom: 1px solid $sv-line;
}

.sv-grid__ophead {
  position: sticky;
  top: 0;
  z-index: 4;
  background: $sv-surface;
  border-bottom: 1px solid $sv-line;
  border-left: 1px solid $sv-line;
  border-top: 3px solid transparent;
  padding: 7px 9px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
}

.sv-grid__opname {
  font-size: 12.5px;
  font-weight: 700;
  color: $sv-ink;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sv-grid__provs {
  display: flex;
  flex: 0 0 auto;
}

.sv-grid__prov {
  width: 20px;
  height: 20px;
  margin-left: -4px;
  border-radius: 50%;
  border: 1.5px solid $sv-surface;
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  line-height: 17px;
  text-align: center;
  text-transform: uppercase;
}

.sv-grid__gutter {
  position: sticky;
  left: 0;
  z-index: 2;
  background: $sv-bg;
}

.sv-grid__hour-label {
  box-sizing: border-box;
  padding: 2px 6px 0 0;
  text-align: right;
  font-size: 10.5px;
  font-weight: 600;
  color: $sv-ink-3;
  border-top: 1px solid $sv-line;
}

.sv-grid__col {
  position: relative;
  border-left: 1px solid $sv-line;
}

.sv-grid__hour-row {
  border-top: 1px solid rgba(21, 32, 26, 0.06);
}

.sv-grid__blockout {
  position: absolute;
  left: 3px;
  right: 3px;
  z-index: 1;
  border-radius: 6px;
  padding: 0 7px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $sv-ink-3;
  overflow: hidden;
  background: repeating-linear-gradient(
    135deg,
    rgba(21, 32, 26, 0.05) 0 7px,
    rgba(21, 32, 26, 0.09) 7px 14px
  );
}

.sv-grid__now {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 2;
  height: 2px;
  background: $sv-brand;
  pointer-events: none;
}

.sv-grid__unplaced {
  margin: 0;
  padding: 8px 12px;
  font-size: 12px;
  color: $sv-ink-3;
  border-top: 1px solid $sv-line;
  background: $sv-surface;
}

@media (max-width: 768px) {
  .sv-grid__ophead { padding: 6px 7px; }
  .sv-grid__opname { font-size: 12px; }
}
</style>
